import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, Plus, RefreshCw, Edit, X, AlertCircle, UserPlus, ChevronLeft, ChevronRight, Eye, EyeOff, CheckCircle, XCircle, Mail, Printer, ChevronDown, ChevronUp, History, Loader2 } from "lucide-react";
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const UserManagement = () => {
    const { language, translations } = useLanguage();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [error, setError] = useState(null);
    
    // --- Loading States ---
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [updatingUserId, setUpdatingUserId] = useState(null);
    
    // UI & Table State
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'designation', direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(10);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // Location State & Cache
    const [districts, setDistricts] = useState([]);
    const [talukasCache, setTalukasCache] = useState({});
    const [villagesCache, setVillagesCache] = useState({});
    const [loadingStates, setLoadingStates] = useState({ districts: false });

    // Modal & Form State
    const [modalConfig, setModalConfig] = useState({ isOpen: false, mode: 'add', data: null });
    const [formErrors, setFormErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    const [formData, setFormData] = useState({
        old_user_id: "", full_name: "", mobile: "", email: "", user_role: "", usr_pwd: "", confirm_pwd: "", 
        designation: "", department: "", district: "", taluka: "", village: "", address: "",
        project_id: "", proposal_id: "", default_project: "", default_proposal: "", sub_user_id: "", work_assign: ""
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // ================= DATA FETCHING =================
    const fetchUsersAndRoles = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [usersRes, rolesRes] = await Promise.all([
                apiService.getAllUser(),
                apiService.getAllUserRoles()
            ]);

            if (usersRes.status === 200) setUsers(usersRes.data);
            else throw new Error("Failed to fetch users");

            if (rolesRes.status === 200 && rolesRes.data) setRoles(rolesRes.data);
        } catch (err) {
            setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    const fetchDistricts = useCallback(async () => {
        setLoadingStates(prev => ({ ...prev, districts: true }));
        try {
            const response = await apiService.getDistricts();
            if (response?.status === 200) setDistricts(response.data || []);
        } catch (e) {
            console.error("Error loading districts:", e);
        } finally {
            setLoadingStates(prev => ({ ...prev, districts: false }));
        }
    }, []);

    useEffect(() => {
        fetchUsersAndRoles();
        fetchDistricts();
    }, [fetchUsersAndRoles, fetchDistricts]);

    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        await fetchUsersAndRoles(true);
        setIsRefreshing(false);
    };

    // ================= LOCATION CASCADING API HELPERS =================
    const fetchTalukas = async (districtName) => {
        if (!districtName || talukasCache[districtName]) return;
        setLoadingStates(prev => ({ ...prev, [`taluka_${districtName}`]: true }));
        try {
            const response = await apiService.getTalukas(districtName);
            if (response?.status === 200) {
                setTalukasCache(prev => ({ ...prev, [districtName]: response.data || [] }));
            }
        } catch (e) {
            console.error("Error loading talukas:", e);
        } finally {
            setLoadingStates(prev => ({ ...prev, [`taluka_${districtName}`]: false }));
        }
    };

    const fetchVillages = async (districtName, talukaName) => {
        const cacheKey = `${districtName}_${talukaName}`;
        if (!districtName || !talukaName || villagesCache[cacheKey]) return;
        setLoadingStates(prev => ({ ...prev, [`village_${cacheKey}`]: true }));
        try {
            const response = await apiService.getVillages(districtName, talukaName);
            if (response?.status === 200) {
                setVillagesCache(prev => ({ ...prev, [cacheKey]: response.data || [] }));
            }
        } catch (e) {
            console.error("Error loading villages:", e);
        } finally {
            setLoadingStates(prev => ({ ...prev, [`village_${cacheKey}`]: false }));
        }
    };

    const showToast = useCallback((message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    }, []);

    // ================= STRICT PAYLOAD BUILDER =================
    const buildSafePayload = (data, isActiveOverride) => {
        let finalIsActive = 1;
        if (isActiveOverride !== undefined) {
            finalIsActive = isActiveOverride ? 1 : 0;
        } else {
            finalIsActive = data.is_active ? 1 : 0;
        }

        const payload = {
            full_name: data.full_name, 
            mobile: data.mobile, 
            email: data.email, 
            user_role: data.user_role ? parseInt(data.user_role, 10) : null,
            designation: data.designation, 
            department: data.department, 
            address: data.address,
            district: data.district, 
            taluka: data.taluka, 
            village: data.village,
            is_active: finalIsActive, 
            project_id: data.project_id, 
            proposal_id: data.proposal_id, 
            default_project: data.default_project, 
            default_proposal: data.default_proposal, 
            sub_user_id: data.sub_user_id,
            work_assign: data.work_assign,
            assign: 1
        };

        if (data.usr_pwd && data.usr_pwd.trim() !== "") payload.usr_pwd = data.usr_pwd;
        if (data.old_user_id) payload.old_user_id = data.old_user_id;

        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
        return payload;
    };

    // ================= DATA PROCESSING & GROUPING =================
    const groupedPositions = useMemo(() => {
        const map = new Map();
        users.forEach(u => {
            const roleId = String(u.user_role || '');
            const key = `${u.email}_${roleId}`;
            
            if (!map.has(key)) {
                map.set(key, {
                    id: key, email: u.email, roleId: roleId, designation: u.designation || u.usr_role,
                    history: [], activeUser: null, latestUser: null
                });
            }
            const group = map.get(key);
            group.history.push(u);
            if (u.is_active === true || u.is_active === 1) group.activeUser = u;
        });

        let result = Array.from(map.values()).map(group => {
            group.history.sort((a, b) => new Date(b.cre_usr_dt || 0) - new Date(a.cre_usr_dt || 0));
            group.latestUser = group.history[0];
            return group;
        });

        if (searchTerm) {
            const needle = searchTerm.toLowerCase();
            result = result.filter(g => 
                g.email?.toLowerCase().includes(needle) || 
                g.designation?.toLowerCase().includes(needle) ||
                g.history.some(h => 
                    h.full_name?.toLowerCase().includes(needle) || 
                    String(h.mobile || '').includes(needle) ||
                    h.district?.toLowerCase().includes(needle) ||
                    h.taluka?.toLowerCase().includes(needle) ||
                    h.village?.toLowerCase().includes(needle)
                )
            );
        }
        return result;
    }, [users, searchTerm]);

    const totalPages = Math.ceil(groupedPositions.length / usersPerPage) || 1;
    const currentPositions = groupedPositions.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

    // ================= HANDLERS =================
    const toggleGroup = (id) => setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));

    const resetForm = useCallback(() => {
        setFormData({ 
            old_user_id: "", full_name: "", mobile: "", email: "", user_role: "", usr_pwd: "", confirm_pwd: "", 
            designation: "", department: "", district: "", taluka: "", village: "", address: "",
            project_id: "", proposal_id: "", default_project: "", default_proposal: "", sub_user_id: "", work_assign: ""
        });
        setFormErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
    }, []);

    const openModal = async (mode, data = null) => {
        resetForm();
        if (mode === 'edit' && data) {
            setFormData({ ...data, usr_pwd: "", confirm_pwd: "" });
            if (data.district) {
                await fetchTalukas(data.district);
                if (data.taluka) await fetchVillages(data.district, data.taluka);
            }
        } else if (mode === 'transfer' && data) {
            // FIX: Safely extract ALL hidden assignment fields from the previous user
            const latest = data.latestUser || {};
            setFormData(prev => ({
                ...prev, 
                old_user_id: latest.usr_id || "", 
                email: data.email || "", 
                user_role: data.roleId || "", 
                designation: data.designation || "",
                department: latest.department || "", 
                district: latest.district || "",
                taluka: latest.taluka || "", 
                village: latest.village || "",
                project_id: latest.project_id || "",         // Crucial for assignment
                proposal_id: latest.proposal_id || "",       // Crucial for assignment
                default_project: latest.default_project || "",
                default_proposal: latest.default_proposal || "",
                sub_user_id: latest.sub_user_id || "",
                work_assign: latest.work_assign || ""
            }));
            
            if (latest.district) {
                await fetchTalukas(latest.district);
                if (latest.taluka) await fetchVillages(latest.district, latest.taluka);
            }
        }
        setModalConfig({ isOpen: true, mode, data });
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        
        if (name === "user_role") {
            const roleObj = roles.find(r => String(r.user_role_id) === value);
            setFormData(prev => ({
                ...prev, user_role: value, 
                designation: roleObj?.usr_role_v1 || roleObj?.usr_role || prev.designation,
                email: roleObj?.email || prev.email
            }));
        } else if (name === "district") {
            setFormData(prev => ({ ...prev, district: value, taluka: "", village: "" }));
            if (value) fetchTalukas(value);
        } else if (name === "taluka") {
            setFormData(prev => ({ ...prev, taluka: value, village: "" }));
            if (value && formData.district) fetchVillages(formData.district, value);
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleToggleStatus = async (user) => {
        setUpdatingUserId(user.usr_id);
        try {
            const isCurrentlyActive = user.is_active === true || user.is_active === 1;
            const payload = buildSafePayload(user, isCurrentlyActive ? 0 : 1);
            
            const response = await apiService.UpdateNewUser(user.usr_id, payload);
            if (response?.status === 200 || response?.status === 201) {
                showToast(`User ${payload.is_active ? 'activated' : 'deactivated'} successfully`, "success");
                await fetchUsersAndRoles(true);
            } else throw new Error(response?.message || "Failed to update status");
        } catch (err) {
            showToast(err.message, "error");
        } finally {
            setUpdatingUserId(null);
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.full_name?.trim()) errors.full_name = "Full name is required";
        if (!formData.email?.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Valid email is required";
        if (!formData.mobile?.trim() || !/^\d{10}$/.test(formData.mobile)) errors.mobile = "10-digit mobile number required";
        if (!formData.user_role) errors.user_role = "Role is required";
        
        if (modalConfig.mode === 'add' || modalConfig.mode === 'transfer') {
            if (!formData.usr_pwd || formData.usr_pwd.length < 6) errors.usr_pwd = "Password must be at least 6 characters";
            if (formData.usr_pwd !== formData.confirm_pwd) errors.confirm_pwd = "Passwords do not match";
        }
        return errors;
    };

  const handleSubmit = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) return setFormErrors(errors);

        setIsSubmitting(true);
        try {
            let response;
            const payload = buildSafePayload(formData, modalConfig.mode === 'edit' ? formData.is_active : 1);
            
            if (modalConfig.mode === 'add' || modalConfig.mode === 'transfer') {
                response = await apiService.addNewUser(payload);
            } else if (modalConfig.mode === 'edit') {
                // FIX: Remove email from the update payload.
                // Since the email cannot be changed during Edit, removing it prevents 
                // the backend from throwing a duplicate email error against the deactivated officer.
                delete payload.email;
                
                response = await apiService.UpdateNewUser(modalConfig.data.usr_id, payload);
            }

            if (response?.status === 200 || response?.status === 201) {
                showToast(`User successfully ${modalConfig.mode === 'edit' ? 'updated' : 'assigned'}`, "success");
                setModalConfig({ isOpen: false, mode: 'add', data: null });
                await fetchUsersAndRoles(true);
            } else throw new Error(response?.message || "Operation failed");
        } catch (err) {
            showToast(err.message || "Something went wrong", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrint = useCallback(() => {
        const activeUsers = users.filter(u => u.is_active === true || u.is_active === 1);

        const printContent = `
        <!DOCTYPE html>
        <html>
            <head>
                <title>Active Users Report</title>
                <style>
                    @page { size: A4 portrait; margin: 0.5in; }
                    body { font-family: 'Arial', sans-serif; }
                    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                    .header h1 { margin: 0; font-size: 24px; color: #2563eb; }
                    table { width: 100%; border-collapse: collapse; font-size: 12px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f8f9fa; font-weight: bold; color: #374151; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                    .role-badge { background-color: #e0f2fe; color: #0284c7; padding: 3px 8px; border-radius: 12px; font-weight: bold; }
                    .status-active { color: #16a34a; font-weight: bold; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Active Users Report</h1>
                    <p>Total Active Officers: ${activeUsers.length}</p>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th style="width: 5%;">No.</th>
                            <th style="width: 25%;">Name</th>
                            <th style="width: 25%;">Email</th>
                            <th style="width: 15%;">Mobile</th>
                            <th style="width: 20%;">Role / Designation</th>
                            <th style="width: 10%;">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${activeUsers.map((user, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td><strong>${user.full_name || 'N/A'}</strong></td>
                                <td>${user.email || 'N/A'}</td>
                                <td>${user.mobile || 'N/A'}</td>
                                <td><span class="role-badge">${user.designation || user.usr_role || 'N/A'}</span></td>
                                <td class="status-active">Active</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
        </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    }, [users]);

    // ================= RENDER HELPERS =================
    if (loading) return (
        <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading User Data...</p>
        </div>
    );

    if (error) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="bg-red-50 p-6 rounded-lg text-red-600 flex items-center shadow-lg"><AlertCircle className="mr-4" />{error}</div>
        </div>
    );

    const activeUsers = users.filter(u => u.is_active === true || u.is_active === 1);

    return (
        <div className="p-6 max-w-full bg-gray-50 min-h-screen">
            {toast.show && (
                <div className={`fixed top-4 right-4 px-6 py-3 rounded-md shadow-lg z-50 flex items-center text-white ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                    {toast.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> : <XCircle className="h-5 w-5 mr-2" />}
                    {toast.message}
                </div>
            )}

            {/* Header / Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 print:hidden">
                <div className="relative w-full md:w-64">
                    <Search className="absolute inset-y-0 left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <input type="text" className="block w-full pl-10 pr-3 h-10 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500" placeholder="Search officers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && <X className="absolute right-3 top-2.5 h-5 w-5 cursor-pointer text-gray-400" onClick={() => setSearchTerm("")} />}
                </div>
                <div className="flex gap-2">
                    <button onClick={handleManualRefresh} disabled={isRefreshing} className="flex items-center justify-center h-10 px-4 border rounded-md text-sm font-medium bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
                        <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin text-blue-600' : ''}`} /> Refresh
                    </button>
                    <button onClick={handlePrint} className="flex items-center justify-center h-10 px-4 border rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50">
                        <Printer className="mr-2 h-4 w-4" /> Print Active
                    </button>
                    <button onClick={() => openModal('add')} className="flex items-center justify-center h-10 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors">
                        <UserPlus className="mr-2 h-4 w-4" /> Add User
                    </button>
                </div>
            </div>

            {/* Print Only Area */}
            <div className="hidden print:block mb-8 border-b-2 border-gray-800 pb-4">
                <h1 className="text-2xl font-bold text-center text-blue-800">Active Users Report</h1>
                <p className="text-center text-gray-600">Total Active Officers: {activeUsers.length}</p>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100 text-gray-600 text-xs uppercase font-medium">
                        <tr>
                            <th className="px-6 py-3 text-left">Role & Office Details</th>
                            <th className="px-6 py-3 text-left">Active Officer Details</th>
                            <th className="px-6 py-3 text-left">Location / Mobile</th>
                            <th className="px-6 py-3 text-center">Status</th>
                            <th className="px-6 py-3 text-center print:hidden">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentPositions.map((group) => (
                            <React.Fragment key={group.id}>
                                <tr className="hover:bg-blue-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <button onClick={() => toggleGroup(group.id)} className="mr-3 text-gray-500 hover:text-blue-600 print:hidden focus:outline-none">
                                                {expandedGroups[group.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                            </button>
                                            <div>
                                                <div className="font-semibold text-gray-900">{group.designation || 'N/A'}</div>
                                                <div className="text-sm text-gray-500 flex items-center mt-1"><Mail size={12} className="mr-1"/>{group.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {group.activeUser ? (
                                            <div>
                                                <div className="font-medium text-gray-800">{group.activeUser.full_name}</div>
                                                {group.activeUser.department && <div className="text-xs text-gray-500">{group.activeUser.department}</div>}
                                            </div>
                                        ) : (
                                            <span className="text-red-500 text-sm font-medium">Post Vacant</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {group.activeUser ? (
                                            <>
                                                <div>{group.activeUser.mobile || '-'}</div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {[group.activeUser.village, group.activeUser.taluka, group.activeUser.district].filter(Boolean).join(', ')}
                                                </div>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {group.activeUser ? (
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                                        ) : (
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Unassigned</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center space-x-2 print:hidden">
                                        {group.activeUser ? (
                                            <>
                                                <button onClick={() => openModal('edit', group.activeUser)} disabled={updatingUserId === group.activeUser.usr_id} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 focus:outline-none disabled:opacity-50" title="Edit Active Officer">
                                                    <Edit size={16} />
                                                </button>
                                                <button onClick={() => handleToggleStatus(group.activeUser)} disabled={updatingUserId === group.activeUser.usr_id} className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 focus:outline-none disabled:opacity-50" title="Deactivate & Relieve Officer">
                                                    {updatingUserId === group.activeUser.usr_id ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                                                </button>
                                            </>
                                        ) : (
                                            <button onClick={() => openModal('transfer', group)} className="p-1.5 bg-green-50 text-green-600 rounded hover:bg-green-100 flex items-center gap-1 mx-auto focus:outline-none" title="Assign New Officer to Post">
                                                <Plus size={16} /> Assign
                                            </button>
                                        )}
                                    </td>
                                </tr>

                                {/* Expanded Timeline View */}
                                {expandedGroups[group.id] && (
                                    <tr className="print:hidden">
                                        <td colSpan="5" className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                            <div className="ml-8 border-l-2 border-blue-200 pl-4 py-2">
                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center"><History size={14} className="mr-2"/> Officer Timeline History</h4>
                                                <div className="space-y-4">
                                                    {group.history.map((histUser, idx) => {
                                                        const isUserActive = histUser.is_active === true || histUser.is_active === 1;
                                                        const startDate = histUser.cre_usr_dt ? new Date(histUser.cre_usr_dt).toLocaleDateString() : 'N/A';
                                                        const endDate = (!isUserActive && histUser.last_upd_dt) ? new Date(histUser.last_upd_dt).toLocaleDateString() : null;

                                                        return (
                                                            <div key={histUser.usr_id} className="relative">
                                                                <div className={`absolute -left-5 mt-1.5 h-2 w-2 rounded-full border-2 border-white ${isUserActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                                                                    <div>
                                                                        <span className={`font-medium text-sm ${isUserActive ? 'text-blue-700' : 'text-gray-700'}`}>{histUser.full_name}</span>
                                                                        <span className="text-xs text-gray-500 ml-2">({histUser.mobile})</span>
                                                                    </div>
                                                                    <div className="text-xs text-right mt-1 sm:mt-0 flex gap-4">
                                                                        <span className="text-gray-500"><span className="font-semibold">From:</span> {startDate}</span>
                                                                        {isUserActive ? (
                                                                            <span className="text-green-600 font-semibold w-24 text-left">Present</span>
                                                                        ) : (
                                                                            <span className="text-gray-500 w-24 text-left"><span className="font-semibold">To:</span> {endDate || 'N/A'}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                        {currentPositions.length === 0 && (
                            <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No records found</td></tr>
                        )}
                    </tbody>
                </table>
                
                {/* Pagination Controls */}
                {groupedPositions.length > 0 && (
                    <div className="px-6 py-4 bg-gray-50 flex items-center justify-between border-t border-gray-200 print:hidden">
                        <span className="text-sm text-gray-700">Showing page {currentPage} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="flex items-center justify-center h-8 w-8 rounded bg-white border disabled:opacity-50"><ChevronLeft size={18} /></button>
                            <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="flex items-center justify-center h-8 w-8 rounded bg-white border disabled:opacity-50"><ChevronRight size={18} /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* Combined Modal for Add / Edit / Transfer */}
            {modalConfig.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-3xl max-h-screen overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                {modalConfig.mode === 'transfer' ? <Plus className="text-green-600"/> : <Edit className="text-blue-600" />}
                                {modalConfig.mode === 'transfer' ? 'Assign New Officer' : modalConfig.mode === 'edit' ? 'Edit User' : 'Add New User'}
                            </h2>
                            <button onClick={() => !isSubmitting && setModalConfig({ isOpen: false, mode: 'add', data: null })} className="text-gray-400 hover:text-gray-700 focus:outline-none disabled:opacity-50"><X size={24} /></button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Role, Designation, Email Row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role*</label>
                                    <select name="user_role" value={formData.user_role} onChange={handleFormChange} disabled={modalConfig.mode === 'transfer' || modalConfig.mode === 'edit' || isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100">
                                        <option value="">Select Role</option>
                                        {roles.map(r => <option key={r.user_role_id} value={r.user_role_id}>{r.usr_role_v1 || r.usr_role}</option>)}
                                    </select>
                                    {formErrors.user_role && <p className="text-red-500 text-xs mt-1">{formErrors.user_role}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} disabled={modalConfig.mode === 'transfer' || modalConfig.mode === 'edit' || isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md disabled:bg-gray-100 focus:ring-2 focus:ring-blue-500" />
                                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                                    <input type="text" name="designation" value={formData.designation} onChange={handleFormChange} disabled={isSubmitting} placeholder="Overrides default role name" className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                </div>
                            </div>

                            <hr className="border-gray-200" />

                            {/* Name, Mobile, Dept Row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                                    <input type="text" name="full_name" value={formData.full_name} onChange={handleFormChange} disabled={isSubmitting} placeholder="John Doe" className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                    {formErrors.full_name && <p className="text-red-500 text-xs mt-1">{formErrors.full_name}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile*</label>
                                    <input type="tel" name="mobile" value={formData.mobile} onChange={handleFormChange} disabled={isSubmitting} placeholder="10 digits" className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                    {formErrors.mobile && <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                                    <input type="text" name="department" value={formData.department} onChange={handleFormChange} disabled={isSubmitting} placeholder="Ex: Revenue" className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                </div>
                            </div>

                            {/* Location Grid Row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">District (जिल्हा)</label>
                                    <select name="district" value={formData.district} onChange={handleFormChange} disabled={loadingStates.districts || isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100">
                                        <option value="">{loadingStates.districts ? "लोड होत आहे..." : "जिल्हा निवडा"}</option>
                                        {districts.map(d => <option key={d.dist_no} value={d.dist_name}>{d.dist_name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Taluka (तालुका)</label>
                                    <select name="taluka" value={formData.taluka} onChange={handleFormChange} disabled={!formData.district || loadingStates[`taluka_${formData.district}`] || isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100">
                                        <option value="">{!formData.district ? "पहिले जिल्हा निवडा" : loadingStates[`taluka_${formData.district}`] ? "लोड होत आहे..." : "तालुका निवडा"}</option>
                                        {(talukasCache[formData.district] || []).map(t => <option key={t.taluka_no} value={t.taluka_name}>{t.taluka_name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Village (गाव)</label>
                                    <select name="village" value={formData.village} onChange={handleFormChange} disabled={!formData.taluka || loadingStates[`village_${formData.district}_${formData.taluka}`] || isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100">
                                        <option value="">{!formData.taluka ? "पहिले तालुका निवडा" : loadingStates[`village_${formData.district}_${formData.taluka}`] ? "लोड होत आहे..." : "गाव निवडा"}</option>
                                        {(villagesCache[`${formData.district}_${formData.taluka}`] || []).map(v => <option key={v.village_id} value={v.village_name}>{v.village_name}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Address Row */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Office Address</label>
                                <textarea name="address" value={formData.address} onChange={handleFormChange} disabled={isSubmitting} rows="2" placeholder="Full address details..." className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                            </div>

                            <hr className="border-gray-200" />

                            {/* Passwords (For Add/Transfer, or Optional for Edit) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Password {modalConfig.mode === 'edit' && '(Optional)'}
                                    </label>
                                    <div className="relative">
                                        <input type={showPassword ? "text" : "password"} name="usr_pwd" value={formData.usr_pwd} onChange={handleFormChange} disabled={isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md pr-10 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isSubmitting} className="absolute right-3 top-2.5 text-gray-400 focus:outline-none disabled:opacity-50"><Eye size={18}/></button>
                                    </div>
                                    {formErrors.usr_pwd && <p className="text-red-500 text-xs mt-1">{formErrors.usr_pwd}</p>}
                                </div>
                                {(modalConfig.mode === 'add' || modalConfig.mode === 'transfer') && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                        <div className="relative">
                                            <input type={showConfirmPassword ? "text" : "password"} name="confirm_pwd" value={formData.confirm_pwd} onChange={handleFormChange} disabled={isSubmitting} className="w-full h-10 px-3 border border-gray-300 rounded-md pr-10 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
                                            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} disabled={isSubmitting} className="absolute right-3 top-2.5 text-gray-400 focus:outline-none disabled:opacity-50"><Eye size={18}/></button>
                                        </div>
                                        {formErrors.confirm_pwd && <p className="text-red-500 text-xs mt-1">{formErrors.confirm_pwd}</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-lg">
                            <button onClick={() => setModalConfig({ isOpen: false, mode: 'add', data: null })} disabled={isSubmitting} className="flex items-center justify-center h-10 px-4 bg-white border border-gray-300 rounded-md font-medium hover:bg-gray-100 transition-colors disabled:opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center justify-center h-10 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors disabled:opacity-75">
                                {isSubmitting ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : modalConfig.mode === 'transfer' ? (
                                    <Plus className="mr-2 h-4 w-4" />
                                ) : (
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                )}
                                {isSubmitting ? 'Saving...' : modalConfig.mode === 'transfer' ? 'Assign & Save' : 'Save Details'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
